import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader, CardTitle, Spinner, Table } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { surfaceLabel } from "../../helpers/experiments";
import { latestRow, fmtPct } from "../../helpers/experimentResults";

const fmtDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date.getTime()) || date.getFullYear() < 1971) return "—";
    return date.toLocaleDateString("fr-FR");
};

// Journal des expériences conclues : la mémoire du programme A/B. On y relit ce
// qui a été testé, ce qui a gagné, de combien, et si le gagnant a été figé dans
// les défauts de la surface (variante en données) ou reste servi par le
// middleware en attendant un déploiement du front (variante codée).
export function ExperimentsJournal() {
    const [experiments, setExperiments] = useState(null);
    const [liftByID, setLiftByID] = useState({});
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        apiFetch(API.back + "/experiments", { method: "GET", market: false })
            .then((res) => {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
            })
            .then((data) => {
                if (cancelled) return;
                const concluded = (Array.isArray(data) ? data : [])
                    .filter((experiment) => experiment.status === "concluded")
                    .sort((a, b) => String(b.endedAt || "").localeCompare(String(a.endedAt || "")));
                setExperiments(concluded);

                concluded.forEach((experiment) => {
                    apiFetch(API.back + "/experiment-results?id=" + encodeURIComponent(experiment.id), {
                        method: "GET",
                        market: false,
                    })
                        .then((res) => (res.ok ? res.json() : []))
                        .then((rows) => {
                            const row = latestRow(rows);
                            if (row && !cancelled) setLiftByID((current) => ({ ...current, [experiment.id]: row }));
                        })
                        .catch((err) => console.log(err));
                });
            })
            .catch((err) => {
                console.log(err);
                if (!cancelled) {
                    setError("Journal indisponible (" + err.message + ").");
                    setExperiments([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const gainCell = (experiment) => {
        const row = liftByID[experiment.id];
        if (!row || !Array.isArray(row.liftCI90)) return "—";
        return (
            <span title="Intervalle de crédibilité à 90 % du gain relatif de B sur A">
                {fmtPct(row.liftCI90[0])} → {fmtPct(row.liftCI90[1])}
            </span>
        );
    };

    return (
        <Card className="flex-fill w-100">
            <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                    <CardTitle tag="h5" className="mb-0">
                        Journal des expériences
                    </CardTitle>
                    <Link to="/experiments/list" className="btn btn-outline-secondary btn-sm">
                        Retour à la liste
                    </Link>
                </div>
                <div className="text-muted small mt-2">
                    Expériences conclues. « Appliqué à la surface » = la config de la variante gagnante est devenue le défaut de
                    la surface, l'expérience n'est plus servie.
                </div>
            </CardHeader>
            <CardBody>
                {error ? <div className="alert alert-danger">{error}</div> : null}
                {!experiments && !error ? (
                    <div className="d-flex align-items-center">
                        <Spinner color="primary" size="sm" />
                        <span className="text-muted ml-2">Chargement…</span>
                    </div>
                ) : null}
                {experiments && experiments.length === 0 && !error ? (
                    <p className="text-muted mb-0">Aucune expérience conclue pour le moment.</p>
                ) : null}
                {experiments && experiments.length > 0 ? (
                    <div style={{ overflowX: "auto" }}>
                        <Table responsive size="sm" className="mb-0">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Surface</th>
                                    <th>Début</th>
                                    <th>Fin</th>
                                    <th>Gagnant</th>
                                    <th>Gain (IC 90 %)</th>
                                    <th>Application</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {experiments.map((experiment) => {
                                    const winner = (experiment.variants || []).find((v) => v.key === experiment.winner);
                                    return (
                                        <tr key={experiment.id}>
                                            <td>
                                                <div>{experiment.name}</div>
                                                <div className="text-muted small">{experiment.hypothesis}</div>
                                            </td>
                                            <td>{surfaceLabel(experiment.surface)}</td>
                                            <td>{fmtDate(experiment.startedAt)}</td>
                                            <td>{fmtDate(experiment.endedAt)}</td>
                                            <td>
                                                {experiment.winner ? (
                                                    <span>
                                                        {experiment.winner}
                                                        {winner && winner.name ? " — " + winner.name : ""}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">aucun</span>
                                                )}
                                            </td>
                                            <td>{gainCell(experiment)}</td>
                                            <td>
                                                {experiment.winnerApplied ? (
                                                    <Badge color="success">appliqué à la surface</Badge>
                                                ) : experiment.winner ? (
                                                    <Badge color="warning">servi à 100 % (variante codée)</Badge>
                                                ) : (
                                                    <span className="text-muted">—</span>
                                                )}
                                            </td>
                                            <td className="table-action">
                                                <Link to={"/experiments/results/" + experiment.id}>Résultats</Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                ) : null}
            </CardBody>
        </Card>
    );
}

export default ExperimentsJournal;
